import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";

/** Shape returned by employee SOP detail API (`getSopDetailForEmployee`). */
export type TrainingSopPdfModel = {
  id: string;
  title: string;
  purpose: string | null;
  scope: string | null;
  updatedAt: Date | string;
  steps: Array<{
    id: string;
    stepNumber: number;
    title: string;
    description: string | null;
    notes: string | null;
  }>;
};

interface TrainingSopPdfPreviewContentProps {
  sop: TrainingSopPdfModel;
}

export function TrainingSopPdfPreviewContent({
  sop,
}: TrainingSopPdfPreviewContentProps) {
  return (
    <div
      id="sop-pdf-content"
      data-tourid="training-sop-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">
            Standard Operating Procedure
          </p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between">
          <div className="text-sm">
            <p className="font-semibold">
              ເອກະສານເລກທີ:{" "}
              <span className="font-normal">{`SOP-${sop.id.slice(-6).toUpperCase()}`}</span>
            </p>
            <p className="mt-1 font-semibold">
              ສະບັບທີ: <span className="font-normal">1.0</span>
            </p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold">ວັນທີອັບເດດລ່າສຸດ:</p>
            <p className="font-normal">{formatDateLocal(sop.updatedAt)}</p>
            <p className="mt-2 font-semibold">ວັນທີພິມ:</p>
            <p className="font-normal">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      <div className="mb-12 text-center">
        <h2 className="mb-6 border-gray-300 border-b-2 pb-4 font-bold text-3xl text-gray-800 uppercase tracking-wide">
          {sop.title}
        </h2>
      </div>

      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          1. ຂໍ້ມູນພື້ນຖານ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຈຸດປະສົງຂອງ SOP
              </td>
              <td className="p-4 text-sm leading-relaxed">
                {sop.purpose?.trim() ? sop.purpose : "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຂອບເຂດການໃຊ້ງານ
              </td>
              <td className="whitespace-pre-line p-4 text-sm leading-relaxed">
                {sop.scope?.trim() ? sop.scope : "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr>
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ວັນທີອັບເດດລ່າສຸດ
              </td>
              <td className="p-4 text-sm">
                {formatDateTimeLocal(sop.updatedAt)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {sop.steps && sop.steps.length > 0 ? (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            2. ຂັ້ນຕອນການເຮັດວຽກ
          </h3>

          <div className="space-y-6">
            {sop.steps.map((step, index) => (
              <div key={step.id} className="relative">
                <div className="mb-3">
                  <h4 className="font-bold text-gray-800 text-lg">
                    2.{step.stepNumber}. {step.title}
                  </h4>
                </div>

                {step.description ? (
                  <div className="mb-4 ml-6">
                    <div
                      className="text-base text-gray-700 leading-relaxed"
                      // Same as admin SOP PDF: step body is stored as HTML
                      dangerouslySetInnerHTML={{ __html: step.description }}
                    />
                  </div>
                ) : null}

                {step.notes ? (
                  <div className="mb-4 ml-6">
                    <div className="text-sm">
                      <span className="font-semibold text-gray-600">
                        ຂໍ້ຄວນລະວັງພິເສດ:{" "}
                      </span>
                      <span className="text-gray-800">{step.notes}</span>
                    </div>
                  </div>
                ) : null}

                {index < sop.steps.length - 1 ? (
                  <div className="mt-6 mb-4 border-gray-200 border-b" />
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="mt-16 border-gray-800 border-t-4 pt-8">
        <div className="grid grid-cols-2 gap-8 text-sm">
          <div>
            <p className="mb-2 font-bold text-gray-800 uppercase tracking-wide">
              ເອກະສານອະນຸມັດໂດຍ
            </p>
            <div className="mb-2 border-gray-400 border-b pb-8" />
            <p className="text-gray-600 text-xs">ຊື່-ນາມສະກຸນ, ຕໍາແໜ່ງ</p>
            <p className="text-gray-600 text-xs">ວັນທີ: _______________</p>
          </div>
          <div>
            <p className="mb-2 font-bold text-gray-800 uppercase tracking-wide">
              ເອກະສານທົບທວນໂດຍ
            </p>
            <div className="mb-2 border-gray-400 border-b pb-8" />
            <p className="text-gray-600 text-xs">ຊື່-ນາມສະກຸນ, ຕໍາແໜ່ງ</p>
            <p className="text-gray-600 text-xs">ວັນທີ: _______________</p>
          </div>
        </div>

        <div className="mt-8 border-gray-300 border-t pt-6 text-center text-gray-500 text-xs">
          <p className="font-medium">
            ເອກະສານນີ້ໄດ້ຮັບການສ້າງຈາກລະບົບພະນັກງານ (ຝຶກອົບຮົມ ແລະ ມາດຕະຖານ) -
            FREEDOM Sole Co.Ltd
          </p>
          <p className="mt-2">
            ເລກເອກະສານ: {`SOP-${sop.id.slice(-6).toUpperCase()}`} | ພິມເມື່ອ:{" "}
            {formatDateLocal(new Date())}
          </p>
        </div>
      </div>
    </div>
  );
}
